// Knowledge pages (04 §Knowledge `pages`; 06 §Main screens "Page"; 05 `page`). M7-02: contracts
// and handlers together. Pages live in spaces, which are base plan (ADR-0016 §8), so `feature:
// core`, like boards. A task's body is a `task_body` page (`POST tasks/{id}/page`).
import { z } from "zod";
import {
  KnowledgeSourceKind,
  PageKind,
  PageMemberLevel,
  PageMemberPrincipalKind,
  PageUploadStatus,
} from "../enums";
import { Page, PageUpload, PageVersion } from "../page-rows";
import { LIMITS, Timestamp, text, Uuid } from "../primitives";
import { BlockNoteDoc } from "../work-rows";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { defineRoute } from "./route";
import {
  SPACE_SHARE_MAX,
  SpaceShareInput,
  SpaceShareList,
  SpaceShareListQuery,
  SpaceShareSet,
} from "./space-shares";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

/** The kinds a caller may give: `task_body` pages are made through their task only. */
export const WritablePageKind = PageKind.exclude(["task_body"]);
/**
 * Starter bodies (program M7-02 "templates (SOP/policy)"; DOCS-KB adds a KB article and meeting
 * notes): set the kind and the first body.
 */
export const PageTemplateKey = z.enum(["sop", "policy", "kb", "meeting"]);

/**
 * UPLOAD-STUCK-FIX: how far the background read of an uploaded document is — a PDF's pages read of
 * its pages (`pages`), otherwise the read's steps (`steps`). Only while `processing`, once the
 * worker has started on it.
 */
export const PageUploadProgress = z
  .object({
    done: z.int().min(0),
    total: z.int().min(1),
    unit: z.enum(["pages", "steps"]),
  })
  .meta({ id: "PageUploadProgress" });
export type PageUploadProgress = z.infer<typeof PageUploadProgress>;

/** A page as trees and lists show it: no body. */
export const PageSummary = Page.pick({
  id: true,
  space_id: true,
  parent_page_id: true,
  kind: true,
  title: true,
  icon: true,
  position: true,
  restricted: true,
  version: true,
  created_by_membership_id: true,
  archived_at: true,
  created_at: true,
  updated_at: true,
})
  .extend({
    /** KB-PDF-UPLOAD: a page made from an uploaded document (`GET pages` only): its type and status. */
    upload: z
      .object({
        mime: z.string(),
        status: PageUploadStatus,
        /** UPLOAD-STUCK-FIX: why the read failed (`failed` only). */
        error: z.string().nullable().optional(),
        /** UPLOAD-STUCK-FIX: how far the read is (`processing` only). */
        progress: PageUploadProgress.optional(),
      })
      .optional(),
  })
  .meta({ id: "PageSummary" });
export type PageSummary = z.infer<typeof PageSummary>;

/**
 * KB-PDF-UPLOAD: upload a document to the Knowledge base. Its text is extracted as Markdown and
 * becomes a new page's body; the original stays downloadable beside it. PDF (text layer; no OCR),
 * DOCX, and plain text or Markdown files, up to `maxMb`.
 */
export const PAGE_UPLOAD_LIMITS = {
  // UPLOAD-STUCK-FIX: 100 MB (was 20: a book-length PDF); the proxy admits it (infra/vps/Caddyfile)
  maxMb: 100,
  maxBytes: 100 * 1024 * 1024,
  /** The multipart request at the cap (the file plus its parts' headers and fields). */
  maxBodyBytes: 100 * 1024 * 1024 + 64 * 1024,
  /** What a page body holds of the Markdown (the whole stays in the `.md` beside the original). */
  bodyMaxChars: 150_000,
  /** The types it reads, and the extensions the file picker offers. */
  mimes: [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "text/plain",
    "text/markdown",
  ],
  accept: ".pdf,.docx,.txt,.md,.markdown",
} as const;

export const PageUploadCreate = z
  .object({
    file: z.string().describe("The document (multipart part `file`)").meta({ format: "binary" }),
    // UPLOAD-PRIVATE-FIRST: optional; without it the doc is private (its maker's personal space)
    // until shared. Agents and older clients still name a space.
    space_id: Uuid.optional().describe(
      "The doc's home space. Omitted: private, in the uploader's personal space (a person only), until shared",
    ),
    parent_page_id: Uuid.optional(),
    kind: WritablePageKind.optional().describe("Default `kb`"),
    title: text(500).optional().describe("Default: the file's name without its extension"),
    // KB-UPLOAD-2: "Add as" and "Share with" in the same request (multipart text parts).
    read_only: z
      .enum(["true", "false"])
      .optional()
      .describe("`true`: read only for everyone but its managers and editor shares"),
    shares: z
      .string()
      .max(20_000)
      .optional()
      .describe(
        "JSON: `[{space_id, level}]`, the spaces it is also shared into (people and DE seats: `PUT pages/{id}/members` next, an access change of its own)",
      ),
  })
  .meta({ id: "PageUploadCreate" });

/** KB-UPLOAD-2: at most this many people and seats per doc. */
export const PAGE_MEMBER_MAX = 50;

/** A person or seat (a DE's, or a person's role) a doc is shared with directly, and the level. */
export const PageMemberInput = z
  .strictObject({
    principal_kind: PageMemberPrincipalKind,
    principal_id: Uuid,
    level: PageMemberLevel,
  })
  .meta({ id: "PageMemberInput" });

export const PageMember = PageMemberInput.extend({ created_at: Timestamp }).meta({
  id: "PageMember",
});
export type PageMember = z.infer<typeof PageMember>;

/** `PUT pages/{id}/members`: the whole set (one left out is removed). */
export const PageMemberSet = z
  .strictObject({ members: z.array(PageMemberInput).max(PAGE_MEMBER_MAX) })
  .meta({ id: "PageMemberSet" });

export const PageMemberList = z.object({ data: z.array(PageMember) }).meta({
  id: "PageMemberList",
});

/** The page's uploaded document as `GET pages/{id}` shows it. */
export const PageUploadView = PageUpload.omit({ company_id: true })
  .extend({
    /** UPLOAD-STUCK-FIX: how far the read is (`processing` only). */
    progress: PageUploadProgress.optional(),
    /** UPLOAD-ORIGINAL: the earlier originals Replace file kept, oldest first. */
    previous_versions: z
      .array(
        z.object({
          revision: z.int().min(1),
          file_name: z.string(),
          mime: z.string(),
          bytes: z.int().min(1),
          uploaded_at: Timestamp,
          replaced_at: Timestamp,
        }),
      )
      .optional(),
  })
  .meta({ id: "PageUploadView" });
export type PageUploadView = z.infer<typeof PageUploadView>;

export const PageUploadContentQuery = z.object({
  /** `original` (default): the uploaded file; `markdown`: the extracted text as a `.md` file. */
  as: z.enum(["original", "markdown"]).optional(),
  /** `true`: always `Content-Disposition: attachment`. */
  download: z.enum(["true", "false"]).optional(),
  /** UPLOAD-ORIGINAL: an earlier original (`previous_versions[].revision`); default the current. */
  revision: z.coerce.number().int().min(1).optional(),
});

/** UPLOAD-ORIGINAL: `POST pages/{id}/upload/replace` — the new original (multipart `file`). */
export const PageUploadReplace = z
  .object({
    file: z
      .string()
      .describe("The new original (multipart part `file`)")
      .meta({ format: "binary" }),
  })
  .meta({ id: "PageUploadReplace" });

/** A heading of the body, in order (06 "Page — BlockNote document with outline"). */
export const PageOutlineItem = z
  .object({ block_id: z.string().nullable(), level: z.int().min(1).max(6), text: z.string() })
  .meta({ id: "PageOutlineItem" });

/**
 * `GET pages/{id}`: the page with its outline, its breadcrumbs (root first; an ancestor the
 * caller may not view reads `{id: null, title: "…"}`), its live children the caller may view (in
 * sibling order), the task it is the body of, and whether the caller may edit it.
 */
export const PageDetail = Page.extend({
  outline: z.array(PageOutlineItem),
  breadcrumbs: z.array(z.object({ id: Uuid.nullable(), title: z.string() })),
  children: z.array(PageSummary),
  task_id: Uuid.nullable(),
  can_edit: z.boolean(),
  /** KB-UPLOAD-2: the caller may comment in its thread (a `reader` may not). */
  can_comment: z.boolean(),
  /** KB-UPLOAD-2: the caller may change its kind, read-only flag and sharing. */
  can_manage: z.boolean(),
  /** KB-PDF-UPLOAD: the document the page was made from, when it was uploaded. */
  upload: PageUploadView.optional(),
}).meta({ id: "PageDetail" });
export type PageDetail = z.infer<typeof PageDetail>;

export const PageCreate = z
  .strictObject({
    space_id: Uuid,
    parent_page_id: Uuid.nullable().optional().describe("A live page of the same space"),
    kind: WritablePageKind.optional().describe("Default `page` (or the template's kind)"),
    title: text(500),
    icon: z.string().max(32).nullable().optional(),
    body: BlockNoteDoc.optional(),
    body_markdown: z.string().max(200_000).optional(),
    template: PageTemplateKey.optional().describe("A starter body; not with `body`"),
    restricted: z
      .boolean()
      .optional()
      .describe("Only the space's editors see it; always true under a restricted parent"),
    position: z.int().min(0).optional().describe("Default: last among its siblings"),
    /** MULTI-SPACE-ACCESS: more spaces it is shared into, each with the level its members get. */
    shares: z
      .array(SpaceShareInput)
      .max(SPACE_SHARE_MAX)
      .optional()
      .describe("Also share with these spaces (spaces the caller may add to; not the home space)"),
  })
  .meta({ id: "PageCreate" });

export const PagePatch = z
  .strictObject({
    title: text(500).optional(),
    icon: z.string().max(32).nullable().optional(),
    body: BlockNoteDoc.optional(),
    body_markdown: z.string().max(200_000).optional(),
    kind: WritablePageKind.optional(),
    restricted: z.boolean().optional().describe("Space editors only; cascades to the subtree"),
    read_only: z
      .boolean()
      .optional()
      .describe("KB-UPLOAD-2: read only for everyone but its managers (its managers only)"),
  })
  .meta({ id: "PagePatch" });

export const PageMove = z
  .strictObject({
    space_id: Uuid.optional().describe("Another space: the page's subtree moves with it"),
    parent_page_id: Uuid.nullable().optional().describe("null: the top level of the space"),
    position: z.int().min(0).optional().describe("Index among the new siblings; default last"),
  })
  .meta({ id: "PageMove" });

export const PageRestore = z.strictObject({ version: z.int().min(1) }).meta({ id: "PageRestore" });

export const PageListQuery = PageQuery.extend({
  space_id: Uuid.optional(),
  parent_page_id: Uuid.optional(),
  kind: WritablePageKind.optional(),
  include_archived: z.enum(["true", "false"]).optional(),
});

/** ACTIONS: `GET pages/deleted` — the deleted pages of the spaces the caller edits. */
export const DeletedPageQuery = PageQuery.extend({ space_id: Uuid.optional() });
export const DeletedPage = PageSummary.extend({ deleted_at: Timestamp }).meta({
  id: "DeletedPage",
});
export type DeletedPage = z.infer<typeof DeletedPage>;

export const PageVersionSummary = PageVersion.omit({ body: true, body_text: true }).meta({
  id: "PageVersionSummary",
});
export const PageVersionParams = z.object({
  co: Uuid,
  id: Uuid,
  version: z.coerce.number().int().min(1),
});

/** `POST pages/search` (04): title and text of the pages the caller can view; a read. */
export const PageSearch = z
  .strictObject({
    query: z.string().trim().min(1).max(200),
    space_id: Uuid.optional(),
    kinds: z.array(WritablePageKind).max(8).optional(),
    cursor: z.string().max(1024).optional(),
    limit: z.int().min(1).max(50).optional(),
  })
  .meta({ id: "PageSearch" });
export const PageHit = PageSummary.extend({
  space_name: z.string(),
  snippet: z.string().max(LIMITS.text).nullable(),
}).meta({ id: "PageHit" });
export type PageHit = z.infer<typeof PageHit>;

/**
 * `GET pages/{id}/readers` (DOCS-KB "Used by: Riley, Sam"): the agents whose knowledge sources
 * cover this page, directly (`page`), through its space (`space`) or its kind (`kind`).
 */
export const PageReader = z
  .object({
    membership_id: Uuid,
    display_name: z.string(),
    kind: z.enum(["digital_employee", "personal_agent"]),
    via: KnowledgeSourceKind,
    last_read_at: Timestamp.nullable(),
  })
  .meta({ id: "PageReader" });
export type PageReader = z.infer<typeof PageReader>;

/**
 * DOCS-ALWAYS-IN-MEMORY: whether a doc is in company memory (Recall). `in_memory`: every space
 * that should hold it does, as it is now; `syncing`: on its way (within a minute for an upload,
 * the sync interval for an edit); `not_in_memory`: nothing will send it, `reason` says why:
 * `private` (a personal space's doc: Recall cannot keep it to its owner yet), `restricted`,
 * `archived`, `opted_out` (its spaces are switched off), `memory_off` (no memory add-on or
 * Recall not set up).
 */
export const PageMemoryState = z
  .enum(["in_memory", "syncing", "not_in_memory"])
  .meta({ id: "PageMemoryState" });
export const PageMemoryReason = z
  .enum(["private", "restricted", "archived", "opted_out", "memory_off"])
  .meta({ id: "PageMemoryReason" });

/** One space's knowledge base the doc belongs in (company admins only). */
export const PageMemorySpace = z
  .object({
    space_id: Uuid,
    name: z.string(),
    /** `home`: the doc's own space; `shared`: a space it is shared into. */
    via: z.enum(["home", "shared"]),
    sync_enabled: z.boolean(),
    /** Recall holds the doc as it is now. */
    current: z.boolean(),
    synced_at: Timestamp.nullable(),
    last_error: z.string().nullable(),
  })
  .meta({ id: "PageMemorySpace" });

export const PageMemoryStatus = z
  .object({
    state: PageMemoryState,
    reason: PageMemoryReason.nullable(),
    /** The detail behind the chip's (i): company admins only; null for everyone else. */
    spaces: z.array(PageMemorySpace).nullable(),
  })
  .meta({ id: "PageMemoryStatus" });
export type PageMemoryStatus = z.infer<typeof PageMemoryStatus>;

/** RECALL-KB-LINKS: a doc company memory links to this one (or mentions it). */
export const PageRelatedLink = z
  .object({ page_id: Uuid, title: z.string(), snippet: z.string() })
  .meta({ id: "PageRelatedLink" });

/** RECALL-KB-LINKS: one version of the doc's note in memory (company admins only). */
export const PageMemoryVersion = z
  .object({
    version: z.int().min(1),
    created_at: z.string().nullable(),
    origin: z.string().nullable(),
    /** Held for review by memory's safety scan: not live until an admin clears it. */
    held: z.boolean(),
    current: z.boolean(),
    /** The version of the doc in Docs that is exactly this text (what a rollback restores), if any. */
    doc_version: z.int().min(1).nullable(),
  })
  .meta({ id: "PageMemoryVersion" });

/**
 * RECALL-KB-LINKS, DOC-LINKS: what links to a doc. Backlinks, links that point nowhere and tags
 * come from CompanyOS itself (`cell.page_link` and the doc's text), so `state` is `ready` for any
 * doc but a task's body (`off`: the panel renders nothing). `memory` says whether the
 * memory-backed extras are live: plain title mentions and, for company admins, the versions memory
 * holds (`off`: memory is off, the doc is not in memory yet, or the read is not there; mentions
 * empty, versions null). Only docs the caller may view are ever named. `review`: whether memory
 * takes an admin's review and rollback yet (`waiting`: not live, nothing is sent).
 */
export const PageRelated = z
  .object({
    state: z.enum(["ready", "off"]),
    memory: z.enum(["ready", "off"]),
    /** Docs that link to this one. */
    backlinks: z.array(PageRelatedLink),
    /** Docs that name this one without linking it (memory). */
    mentions: z.array(PageRelatedLink),
    /** Links in this doc to docs that do not exist (the link's text, or the address). */
    unresolved: z.array(z.string()),
    tags: z.array(z.string()),
    /** Company admins only; null for everyone else. */
    versions: z.array(PageMemoryVersion).nullable(),
    review: z.enum(["waiting", "ready"]),
  })
  .meta({ id: "PageRelated" });
export type PageRelated = z.infer<typeof PageRelated>;
export type PageRelatedLink = z.infer<typeof PageRelatedLink>;
export type PageMemoryVersion = z.infer<typeof PageMemoryVersion>;

/**
 * WIKI-LINKS-GRAPH: a link this doc makes (`cell.page_link`). `ok`: a live doc the caller may
 * open, with its live title; `missing`: no live doc answers (a `[[Title]]` nobody wrote yet, or a
 * deleted doc; `page_id` null for the first). A link to a doc the caller may not open is left out.
 */
export const PageOutLink = z
  .object({
    page_id: Uuid.nullable(),
    /** The live title (`ok`), else the text the link was written with. */
    title: z.string(),
    state: z.enum(["ok", "missing"]),
  })
  .meta({ id: "PageOutLink" });
export type PageOutLink = z.infer<typeof PageOutLink>;

export const PageLinksQuery = z.strictObject({
  /** `true`: also the docs that name this one's title without linking it (a text search). */
  mentions: z.enum(["true", "false"]).optional(),
});

/** WIKI-LINKS-GRAPH: the doc's outgoing links, backlinks and (on demand) unlinked mentions. */
export const PageLinks = z
  .object({
    outgoing: z.array(PageOutLink),
    backlinks: z.array(PageRelatedLink),
    /** Null unless asked for (`mentions=true`). */
    mentions: z.array(PageRelatedLink).nullable(),
  })
  .meta({ id: "PageLinks" });
export type PageLinks = z.infer<typeof PageLinks>;

/** WIKI-LINKS-GRAPH: a node of a link graph; `missing` nodes are links that point nowhere. */
export const PageGraphNode = z
  .object({
    /** The doc's id; a missing node's is `missing:` + its id or lower-cased title. */
    id: z.string(),
    title: z.string(),
    space_id: Uuid.nullable(),
    kind: z.string().nullable(),
    /** Links in and out among the graph's own nodes. */
    degree: z.int().min(0),
    missing: z.boolean(),
  })
  .meta({ id: "PageGraphNode" });
export const PageGraphEdge = z
  .object({ source: z.string(), target: z.string() })
  .meta({ id: "PageGraphEdge" });

/** WIKI-LINKS-GRAPH: only docs the caller may open; edges to any other doc are dropped. */
export const PageGraph = z
  .object({
    nodes: z.array(PageGraphNode),
    edges: z.array(PageGraphEdge),
    /** More docs matched than the cap: the best-linked are kept. */
    truncated: z.boolean(),
  })
  .meta({ id: "PageGraph" });
export type PageGraph = z.infer<typeof PageGraph>;
export type PageGraphNode = z.infer<typeof PageGraphNode>;

export const PAGE_GRAPH_MAX = 2000;
export const PAGE_LOCAL_GRAPH_MAX = 300;

export const PageLocalGraphQuery = z.strictObject({
  depth: z.enum(["1", "2"]).optional(),
});

export const PageGraphQuery = z.strictObject({
  space_id: Uuid.optional(),
  /** A `#tag` (without the #) the docs' text carries. */
  tag: z
    .string()
    .trim()
    .max(64)
    .regex(/^[\p{L}][\p{L}\p{N}_/-]*$/u)
    .optional(),
  /** `only`: docs with no links among the graph's docs; `hide`: leave them out. */
  orphans: z.enum(["show", "hide", "only"]).optional(),
  limit: z.coerce.number().int().min(1).max(PAGE_GRAPH_MAX).optional(),
});

export const PageMemoryReviewInput = z
  .strictObject({
    action: z.enum(["clear", "reject", "rollback"]),
    version: z.int().min(1),
  })
  .meta({ id: "PageMemoryReviewInput" });

/** `waiting`: memory does not take it yet (nothing was sent). */
export const PageMemoryReview = z
  .object({ result: z.enum(["done", "waiting"]) })
  .meta({ id: "PageMemoryReview" });
export type PageMemoryReview = z.infer<typeof PageMemoryReview>;

/** RECALL-KB-LINKS: a space's "About these docs", for the Docs home and its tags filter. */
export const SpaceDocsMemory = z
  .object({
    state: z.enum(["ready", "off"]),
    /** Docs of the space in memory that the caller may view. */
    docs: z.int().min(0),
    /** Searchable parts memory made of them, when it says. */
    parts: z.int().min(0).nullable(),
    synced_at: z.string().nullable(),
    /** `more`: memory may hold more docs with the tag than the list names (it read a bounded part). */
    tags: z.array(z.object({ tag: z.string(), page_ids: z.array(Uuid), more: z.boolean() })),
    unresolved: z.array(
      z.object({
        target: z.string(),
        pages: z.array(z.object({ page_id: Uuid, title: z.string() })),
      }),
    ),
  })
  .meta({ id: "SpaceDocsMemory" });
export type SpaceDocsMemory = z.infer<typeof SpaceDocsMemory>;

const pageSub = (path: string) => `${base}/pages/{id}/${path}`;

export const pageRoutes = {
  "pages.list": defineRoute({
    ...core,
    id: "pages.list",
    method: "get",
    path: `${base}/pages`,
    tag: "pages",
    summary: "Pages the caller can view (of one space, under one parent, of one kind)",
    access: "member",
    params: CompanyParams,
    query: PageListQuery,
    response: { status: 200, schema: page(PageSummary) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "pages.create": defineRoute({
    ...core,
    id: "pages.create",
    method: "post",
    path: `${base}/pages`,
    tag: "pages",
    summary: "Create a page in a space (blank, from markdown, or from the SOP/policy template)",
    access: "member",
    params: CompanyParams,
    body: PageCreate,
    idempotency: "optional",
    response: { status: 201, schema: PageDetail },
    errors: [],
    emits: ["page.created", "page.shares_changed"],
  }),
  "pages.search": defineRoute({
    ...core,
    id: "pages.search",
    method: "post",
    path: `${base}/pages/search`,
    tag: "pages",
    summary: "Search the title and text of the pages the caller can view",
    access: "member",
    params: CompanyParams,
    body: PageSearch,
    safe: true,
    response: { status: 200, schema: page(PageHit) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "pages.get": defineRoute({
    ...core,
    id: "pages.get",
    method: "get",
    path: `${base}/pages/{id}`,
    tag: "pages",
    summary: "Get a page with its outline, breadcrumbs and children",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: PageDetail },
    errors: [],
    emits: [],
  }),
  "pages.update": defineRoute({
    ...core,
    id: "pages.update",
    method: "patch",
    path: `${base}/pages/{id}`,
    tag: "pages",
    summary: "Edit a page's title, body, icon, kind or restriction (a new version)",
    access: "member",
    params: CompanyItemParams,
    body: PagePatch,
    ifMatch: true,
    response: { status: 200, schema: PageDetail },
    errors: [],
    emits: ["page.edited"],
  }),
  "pages.move": defineRoute({
    ...core,
    id: "pages.move",
    method: "post",
    path: pageSub("move"),
    tag: "pages",
    summary: "Move a page under another parent, to another space or among its siblings",
    access: "member",
    params: CompanyItemParams,
    body: PageMove,
    response: { status: 200, schema: PageDetail },
    errors: [],
    emits: ["page.moved"],
  }),
  "pages.archive": defineRoute({
    ...core,
    id: "pages.archive",
    method: "post",
    path: pageSub("archive"),
    tag: "pages",
    summary: "Archive a page (its child pages move up to its parent)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: PageDetail },
    errors: [],
    emits: ["page.archived"],
  }),
  // DOCS-KB: back from the archive, soft delete, and the agents that read the page.
  "pages.unarchive": defineRoute({
    ...core,
    id: "pages.unarchive",
    method: "post",
    path: pageSub("unarchive"),
    tag: "pages",
    summary: "Restore an archived page (under its old parent when that is live, else at the top)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: PageDetail },
    errors: [],
    emits: ["page.unarchived"],
  }),
  "pages.delete": defineRoute({
    ...core,
    id: "pages.delete",
    method: "delete",
    path: `${base}/pages/{id}`,
    tag: "pages",
    summary: "Delete a page (editors): it leaves every list; its history stays",
    access: "member",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["page.deleted"],
  }),
  // ACTIONS: a wrong delete is undone by the space's editors (the soft delete kept the row).
  "pages.deleted": defineRoute({
    ...core,
    id: "pages.deleted",
    method: "get",
    path: `${base}/pages/deleted`,
    tag: "pages",
    summary: "Deleted pages of the spaces the caller edits (their versions kept)",
    access: "member",
    params: CompanyParams,
    query: DeletedPageQuery,
    response: { status: 200, schema: page(DeletedPage) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "pages.undelete": defineRoute({
    ...core,
    id: "pages.undelete",
    method: "post",
    path: pageSub("undelete"),
    tag: "pages",
    summary:
      "Bring a deleted page back (space editors): live under its old parent when it was live, else archived; its children and agent readers stay where the delete left them",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: PageDetail },
    errors: ["conflict"],
    emits: ["page.undeleted"],
  }),
  // KB-PDF-UPLOAD: a document uploaded to the Knowledge base, its text as the page's Markdown.
  "pages.upload": defineRoute({
    ...core,
    id: "pages.upload",
    method: "post",
    path: `${base}/pages/upload`,
    tag: "pages",
    summary:
      "Upload a PDF, DOCX or text file as a new page (private to the uploader unless a space is named or it is shared): the original kept, its text read as Markdown in the background (`upload.status` processing → done)",
    access: "member",
    params: CompanyParams,
    body: PageUploadCreate,
    bodyType: "multipart",
    idempotency: "optional",
    response: { status: 201, schema: PageDetail },
    errors: ["payload_too_large"],
    emits: [
      "page.created",
      "page.edited",
      "page.shares_changed",
      "page.upload_queued",
      "space.created",
    ],
  }),
  "pages.uploadRetry": defineRoute({
    ...core,
    id: "pages.uploadRetry",
    method: "post",
    path: pageSub("upload/retry"),
    tag: "pages",
    summary:
      "Queue an uploaded page's original to be read again (back to processing; the worker replaces its empty body with the new Markdown)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: PageDetail },
    errors: [],
    emits: ["page.upload_queued"],
  }),
  "pages.uploadContent": defineRoute({
    ...core,
    id: "pages.uploadContent",
    method: "get",
    path: pageSub("upload/content"),
    tag: "pages",
    summary: "An uploaded page's original file, or its extracted Markdown as a .md file",
    access: "member",
    params: CompanyItemParams,
    query: PageUploadContentQuery,
    response: { status: 200, contentType: "application/octet-stream" },
    errors: [],
    emits: [],
  }),
  // UPLOAD-ORIGINAL (founder 2026-10-30): the doc's managers manage its original file.
  "pages.uploadReplace": defineRoute({
    ...core,
    id: "pages.uploadReplace",
    method: "post",
    path: pageSub("upload/replace"),
    tag: "pages",
    summary:
      "Replace an uploaded doc's original with a new file (the old one kept as a previous version); its text is read again in the background and becomes a new version of the doc",
    access: "member",
    params: CompanyItemParams,
    body: PageUploadReplace,
    bodyType: "multipart",
    idempotency: "optional",
    response: { status: 200, schema: PageDetail },
    errors: ["payload_too_large", "conflict"],
    emits: ["page.upload_changed", "page.upload_queued"],
  }),
  "pages.uploadReextract": defineRoute({
    ...core,
    id: "pages.uploadReextract",
    method: "post",
    path: pageSub("upload/reextract"),
    tag: "pages",
    summary:
      "Read an uploaded doc's original again in the background; its text replaces the doc's as a new version",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: PageDetail },
    errors: ["conflict"],
    emits: ["page.upload_queued"],
  }),
  "pages.uploadRemoveOriginal": defineRoute({
    ...core,
    id: "pages.uploadRemoveOriginal",
    method: "delete",
    path: pageSub("upload/original"),
    tag: "pages",
    summary:
      "Remove an uploaded doc's original file and its earlier versions for good; the doc's text stays",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: PageDetail },
    errors: ["conflict"],
    emits: ["page.upload_changed"],
  }),
  "pages.uploadDetach": defineRoute({
    ...core,
    id: "pages.uploadDetach",
    method: "delete",
    path: pageSub("upload"),
    tag: "pages",
    summary:
      "Convert an uploaded doc to a plain doc: its original, earlier versions and .md are removed for good; the text stays",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: PageDetail },
    errors: ["conflict"],
    emits: ["page.upload_changed"],
  }),
  "pages.shares.list": defineRoute({
    ...core,
    id: "pages.shares.list",
    method: "get",
    path: `${base}/page-shares`,
    tag: "pages",
    summary: "Pages shared into more spaces (MULTI-SPACE-ACCESS): the sidebars' Also-in rows",
    description:
      "Every share the caller can see: of a page it can view, into a space it can view. `object_id` narrows it to one page.",
    access: "member",
    params: CompanyParams,
    query: SpaceShareListQuery,
    response: { status: 200, schema: SpaceShareList },
    errors: [],
    emits: [],
  }),
  "pages.shares.set": defineRoute({
    ...core,
    id: "pages.shares.set",
    method: "put",
    path: pageSub("shares"),
    tag: "pages",
    summary: "Share a page into more spaces, each with a level (the page's editors)",
    description:
      "The whole set among the spaces the caller may add pages to: a share left out is removed; shares into other spaces stay. Not the home space, a personal space or an archived one, and a doc's levels are `editor` and `viewer` (422); not a task's body, a board's note or a personal space's page. Members of a shared space get the level on the page: `editor` edits it, `viewer` reads and comments (a restricted page stays its editors'). A share reaches that page only: moving, archiving, deleting, restricting and sharing stay with the home space's editors (403).",
    access: "member",
    params: CompanyItemParams,
    body: SpaceShareSet,
    response: { status: 200, schema: SpaceShareList },
    errors: [],
    emits: ["page.shares_changed"],
  }),
  // KB-UPLOAD-2: a doc shared with people and seats (DEs) directly, each with a level.
  "pages.members.list": defineRoute({
    ...core,
    id: "pages.members.list",
    method: "get",
    path: pageSub("members"),
    tag: "pages",
    summary: "The people and seats (DEs) a page is shared with directly, with their levels",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: PageMemberList },
    errors: [],
    emits: [],
  }),
  "pages.members.set": defineRoute({
    ...core,
    id: "pages.members.set",
    method: "put",
    path: pageSub("members"),
    tag: "pages",
    summary: "Share a page with people and seats (DEs) directly, each with a level (its managers)",
    description:
      "The whole set: one left out is removed. Levels: `reader` reads only (no comments), `viewer` reads and comments, `editor` edits. A person is a membership id, a seat any seat (a DE reaches what its seat is given). Not a task's body, a board's note, or a personal space's page.",
    access: "member",
    params: CompanyItemParams,
    body: PageMemberSet,
    response: { status: 200, schema: PageMemberList },
    errors: [],
    emits: ["page.members_changed", "access.changed"],
  }),
  "pages.readers": defineRoute({
    ...core,
    id: "pages.readers",
    method: "get",
    path: pageSub("readers"),
    tag: "pages",
    summary: "The agents whose knowledge base includes this page",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: z.object({ data: z.array(PageReader) }) },
    errors: [],
    emits: [],
  }),
  "pages.memory": defineRoute({
    ...core,
    id: "pages.memory",
    method: "get",
    path: pageSub("memory"),
    tag: "pages",
    summary: "Whether the doc is in company memory (In memory / Syncing), with admin detail",
    description:
      "Read from CompanyOS's own sync records, never from Recall. Anyone who can view the page reads the state; the per-space detail is for company admins.",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: PageMemoryStatus },
    errors: [],
    emits: [],
  }),
  "pages.related": defineRoute({
    ...core,
    id: "pages.related",
    method: "get",
    path: pageSub("related"),
    tag: "pages",
    summary: "What links to the doc (backlinks, links that point nowhere, tags; memory mentions)",
    description:
      "Backlinks, links that point nowhere and tags are read from CompanyOS (the links saved from each doc's body); mentions and memory versions from the space's knowledge base when memory is on (`memory: off` otherwise). Only docs the caller may view are named. `state: off` only for a task's body; never an error for a memory that is off or slow.",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: PageRelated },
    errors: [],
    emits: [],
  }),
  "pages.links": defineRoute({
    ...core,
    id: "pages.links",
    method: "get",
    path: pageSub("links"),
    tag: "pages",
    summary: "The doc's links out (live titles), backlinks and, on demand, unlinked mentions",
    description:
      "From the links saved with each doc's body. Only docs the caller may open are named: a link to a hidden doc is left out, never called missing. `mentions=true` adds the docs whose text names this doc's title without linking it (a text search, at most 20).",
    access: "member",
    params: CompanyItemParams,
    query: PageLinksQuery,
    response: { status: 200, schema: PageLinks },
    errors: [],
    emits: [],
  }),
  "pages.graph": defineRoute({
    ...core,
    id: "pages.graph",
    method: "get",
    path: pageSub("graph"),
    tag: "pages",
    summary: "The doc's local link graph (1 or 2 hops), docs the caller may open only",
    description:
      "Links in both directions. A hidden doc is no node and no hop. Links from the doc that point nowhere are `missing` nodes. At most 300 nodes.",
    access: "member",
    params: CompanyItemParams,
    query: PageLocalGraphQuery,
    response: { status: 200, schema: PageGraph },
    errors: [],
    emits: [],
  }),
  "pages.graphAll": defineRoute({
    ...core,
    id: "pages.graphAll",
    method: "get",
    path: `${base}/pages/graph`,
    tag: "pages",
    summary: "The link graph of the docs the caller may open (a space's or the company's)",
    description:
      "Live, unarchived docs only, filtered by space, `#tag` and orphans; capped (default and most 2,000 nodes, the best-linked first, `truncated` when more matched). Edges only between returned docs.",
    access: "member",
    params: CompanyParams,
    query: PageGraphQuery,
    response: { status: 200, schema: PageGraph },
    errors: [],
    emits: [],
  }),
  "pages.memoryReview": defineRoute({
    ...core,
    id: "pages.memoryReview",
    method: "post",
    path: pageSub("memory-review"),
    tag: "pages",
    summary: "Clear or reject a held version in memory, or roll memory back (company admins)",
    description:
      "Acts for the admin in memory. `waiting` until the memory service takes people's review and rollback; nothing is sent then. A rollback changes memory only: the client restores the doc to the matching version first (`pages.restore`), so the next sync carries the same text.",
    access: "member",
    params: CompanyItemParams,
    body: PageMemoryReviewInput,
    response: { status: 200, schema: PageMemoryReview },
    errors: ["forbidden", "conflict", "not_found"],
    // acts on memory only (Recall's own audit records it): no CompanyOS state, no event
    safe: true,
    emits: [],
  }),
  "spaces.docsMemory": defineRoute({
    ...core,
    id: "spaces.docsMemory",
    method: "get",
    path: `${base}/spaces/{id}/docs-memory`,
    tag: "pages",
    summary: 'A space\'s "About these docs": docs in memory, tags, unresolved links',
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: SpaceDocsMemory },
    errors: [],
    emits: [],
  }),
  "pages.children": defineRoute({
    ...core,
    id: "pages.children",
    method: "get",
    path: pageSub("children"),
    tag: "pages",
    summary: "A page's live child pages the caller can view, in order",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: z.object({ data: z.array(PageSummary) }) },
    errors: [],
    emits: [],
  }),
  "pages.versions": defineRoute({
    ...core,
    id: "pages.versions",
    method: "get",
    path: pageSub("versions"),
    tag: "pages",
    summary: "A page's versions, newest first",
    access: "member",
    params: CompanyItemParams,
    query: PageQuery,
    response: { status: 200, schema: page(PageVersionSummary) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "pages.version": defineRoute({
    ...core,
    id: "pages.version",
    method: "get",
    path: pageSub("versions/{version}"),
    tag: "pages",
    summary: "One version of a page, with its body",
    access: "member",
    params: PageVersionParams,
    response: { status: 200, schema: PageVersion },
    errors: [],
    emits: [],
  }),
  "pages.restore": defineRoute({
    ...core,
    id: "pages.restore",
    method: "post",
    path: pageSub("restore"),
    tag: "pages",
    summary: "Make an earlier version the current one (written as a new version)",
    access: "member",
    params: CompanyItemParams,
    body: PageRestore,
    response: { status: 200, schema: PageDetail },
    errors: [],
    emits: ["page.edited"],
  }),
  "tasks.page": defineRoute({
    ...core,
    id: "tasks.page",
    method: "post",
    path: `${base}/tasks/{id}/page`,
    tag: "pages",
    summary: "The task's body page, created empty when it has none",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: PageDetail },
    errors: [],
    emits: ["page.created"],
  }),
} as const;
